import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Caricamento, Campo, Errore, Etichetta, Importo, LinkFattura, Scheda, Vuoto, useCarico } from '../componenti/base';
import { etichettaStato, formattaData, formattaEuro } from '../formato';
import { useSessione } from '../main';

interface DocumentoRitenuta {
  id: string;
  numero: string;
  tipo: 'ATTIVA' | 'PASSIVA';
  dataCompetenza: string;
  dataCassaEffettiva: string | null;
  imponibileCent: number;
  totaleCent: number;
  ritenutaCent: number;
  stato: string;
  controparte: string | null;
}

interface Risposta {
  anno: number;
  anniDisponibili: number[];
  subite: { documenti: DocumentoRitenuta[]; totaleCent: number };
  operate: { documenti: DocumentoRitenuta[]; totaleCent: number };
  nonAncoraIncassate: { documenti: DocumentoRitenuta[]; totaleCent: number };
}

export function Ritenute() {
  const { organizzazione } = useSessione();
  const [anno, setAnno] = useState<number | ''>('');
  const { dati, caricamento, errore, ricarica } = useCarico(
    () => api.get<Risposta>(`/api/ritenute${anno ? `?anno=${anno}` : ''}`),
    [anno],
  );

  if (caricamento) return <Caricamento />;
  if (errore || !dati)
    return (
      <div className="p-6">
        <Errore testo={errore ?? 'Caricamento non riuscito'} riprova={ricarica} />
      </div>
    );

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Ritenute d’acconto</h1>
        <p className="text-sm text-inchiostro-500">
          Vista fiscale, non di cassa: qui non ci sono soldi che entrano o escono dalla banca, ci sono le quote
          trattenute. L’anno è quello dell’<strong>incasso</strong>, non dell’emissione — la ritenuta si certifica
          quando la fattura è pagata.
        </p>
      </header>

      {dati.anniDisponibili.length > 1 && (
        <Scheda>
          <Campo etichetta="Anno" larghezza="w-40">
            <select className="campo" value={anno || dati.anno} onChange={(e) => setAnno(Number(e.target.value))}>
              {dati.anniDisponibili.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Campo>
        </Scheda>
      )}

      <Tabella
        titolo={`Ritenute subite nel ${dati.anno}`}
        sottotitolo="Trattenute dai tuoi clienti sulle fatture attive e versate da loro all’erario: le scarichi in dichiarazione"
        totaleCent={dati.subite.totaleCent}
        documenti={dati.subite.documenti}
        vuoto="Nessuna fattura attiva con ritenuta incassata in quest’anno."
        formatoData={organizzazione.formatoData}
      />

      {dati.operate.documenti.length > 0 && (
        <Tabella
          titolo={`Ritenute operate nel ${dati.anno}`}
          sottotitolo="Qui il sostituto d’imposta sei tu: questa quota non la paghi al fornitore, la versi con F24 — è un’uscita che Mastro non vede"
          totaleCent={dati.operate.totaleCent}
          documenti={dati.operate.documenti}
          vuoto=""
          attenzione
          formatoData={organizzazione.formatoData}
        />
      )}

      {dati.nonAncoraIncassate.documenti.length > 0 && (
        <Tabella
          titolo="Non ancora incassate"
          sottotitolo="La ritenuta c’è ma non è ancora certificabile: entrerà nell’anno in cui la fattura verrà pagata"
          totaleCent={dati.nonAncoraIncassate.totaleCent}
          documenti={dati.nonAncoraIncassate.documenti}
          vuoto=""
          formatoData={organizzazione.formatoData}
        />
      )}
    </div>
  );
}

function Tabella({
  titolo,
  sottotitolo,
  totaleCent,
  documenti,
  vuoto,
  attenzione,
  formatoData,
}: {
  titolo: string;
  sottotitolo: string;
  totaleCent: number;
  documenti: DocumentoRitenuta[];
  vuoto: string;
  attenzione?: boolean;
  formatoData: 'gg/mm/aaaa' | 'gg-mm-aaaa' | 'aaaa-mm-gg';
}) {
  return (
    <Scheda
      titolo={titolo}
      sottotitolo={sottotitolo}
      senzaPadding
      azioni={
        <span className={`num text-base font-semibold ${attenzione ? 'text-attenzione' : ''}`}>
          {formattaEuro(totaleCent)}
        </span>
      }
    >
      {documenti.length === 0 ? (
        <Vuoto titolo="Niente da mostrare" testo={vuoto} />
      ) : (
        <div className="overflow-x-auto">
          <table className="dati">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Controparte</th>
                <th>Competenza</th>
                <th>Incassata il</th>
                <th className="text-right">Imponibile</th>
                <th className="text-right">Totale</th>
                <th className="text-right">Ritenuta</th>
              </tr>
            </thead>
            <tbody>
              {documenti.map((d) => (
                <tr key={d.id}>
                  <td>
                    <LinkFattura id={d.id} className="underline underline-offset-2">
                      {d.numero}
                    </LinkFattura>
                  </td>
                  <td className="max-w-72 truncate" title={d.controparte ?? ''}>
                    {d.controparte ?? '—'}
                  </td>
                  <td className="num whitespace-nowrap">{formattaData(d.dataCompetenza, formatoData)}</td>
                  <td className="num whitespace-nowrap">
                    {d.dataCassaEffettiva ? (
                      formattaData(d.dataCassaEffettiva, formatoData)
                    ) : (
                      <Etichetta testo={etichettaStato(d.stato, d.tipo)} tono="attenzione" />
                    )}
                  </td>
                  <td className="text-right">
                    <Importo cent={d.imponibileCent} />
                  </td>
                  <td className="text-right">
                    <Importo cent={d.totaleCent} />
                  </td>
                  <td className="text-right font-medium">
                    <Importo cent={d.ritenutaCent} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Scheda>
  );
}
